<script setup>
import { computed, onMounted, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import AiBotIntentSetsAPI from 'dashboard/api/aiBot/intentSets';
import IntentStats from '../components/IntentStats.vue';
import IntentFeedbackForm from '../components/IntentFeedbackForm.vue';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const INTENT_SOURCES = ['jev', 'llm_fallback', 'none'];
const store = useStore();

const records = useMapGetter('aiBotIntentDecisions/getRecords');
const uiFlags = useMapGetter('aiBotIntentDecisions/getUIFlags');
const meta = useMapGetter('aiBotIntentDecisions/getMeta');

const filters = ref({
  route: '',
  answered_by: '',
  feedback: '',
  max_confidence: '',
  lost: false,
  from: '',
  to: '',
});
const page = ref(1);
const stats = ref(null);
const routeOptions = ref([]);
const expandedId = ref(null);

// Maps the filter bar state to the intent_decisions index query params
// (CONTRACTS §9). Kept pure so the mapping is unit-testable.
const buildFilterParams = activeFilters => {
  const params = {};
  if (activeFilters.route) params.route = activeFilters.route;
  if (activeFilters.answered_by) params.answered_by = activeFilters.answered_by;
  if (activeFilters.feedback) params.feedback = activeFilters.feedback;
  if (
    activeFilters.max_confidence !== '' &&
    activeFilters.max_confidence != null
  ) {
    params.max_confidence = activeFilters.max_confidence;
  }
  if (activeFilters.lost) params.lost = true;
  if (activeFilters.from) params.from = activeFilters.from;
  if (activeFilters.to) params.to = activeFilters.to;
  return params;
};

const sourceLabel = source =>
  INTENT_SOURCES.includes(source)
    ? t(`AI_AGENT.INTENT.SOURCES.${source.toUpperCase()}`)
    : source;

const answeredByOptions = computed(() =>
  INTENT_SOURCES.map(value => ({
    value,
    label: sourceLabel(value),
  }))
);

const feedbackFilterOptions = computed(() =>
  ['none', 'correct', 'wrong'].map(value => ({
    value,
    label: t(`AI_AGENT.INTENT.FEEDBACK.${value.toUpperCase()}`),
  }))
);

const tableHeaders = computed(() => [
  t('AI_AGENT.INTENT.TABLE.HEADERS.TIME'),
  t('AI_AGENT.INTENT.TABLE.HEADERS.MESSAGE'),
  t('AI_AGENT.INTENT.TABLE.HEADERS.ROUTE'),
  t('AI_AGENT.INTENT.TABLE.HEADERS.MOOD'),
  t('AI_AGENT.INTENT.TABLE.HEADERS.TARGET'),
  t('AI_AGENT.INTENT.TABLE.HEADERS.APPLIED'),
  t('AI_AGENT.INTENT.TABLE.HEADERS.ANSWERED_BY'),
  t('AI_AGENT.INTENT.TABLE.HEADERS.LATENCY'),
  t('AI_AGENT.INTENT.TABLE.HEADERS.FEEDBACK'),
]);

const totalPages = computed(() =>
  meta.value.total_count ? Math.ceil(meta.value.total_count / 25) : 1
);

const feedbackLabel = feedback =>
  t(`AI_AGENT.INTENT.FEEDBACK.${(feedback || 'NONE').toUpperCase()}`);

const moodPercent = value => `${Math.round((value || 0) * 100)}%`;

const moodBars = decision => [
  { key: 'wants_human', value: moodPercent(decision.wants_human) },
  { key: 'is_complaint', value: moodPercent(decision.is_complaint) },
  { key: 'distress', value: moodPercent(decision.distress) },
];

const appliedMark = decision => (decision.applied ? '✓' : '—');

const percentLabel = value => `${Math.round((value || 0) * 100)}%`;

const routeBadge = decision =>
  `${decision.route} ${percentLabel(decision.confidence)}`;

const latencyLabel = decision =>
  decision.latency_ms == null ? '—' : `${decision.latency_ms} ms`;

const formatTime = value =>
  value ? new Date(value * 1000).toLocaleString() : '—';

const targetLabel = decision =>
  [decision.target_type, decision.target_label]
    .filter(part => part && part !== 'none')
    .join(' · ') || '—';

const probabilityRows = decision =>
  Object.entries(decision.probabilities || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

const fetchDecisions = async () => {
  await store.dispatch('aiBotIntentDecisions/get', {
    botId: props.bot.id,
    page: page.value,
    ...buildFilterParams(filters.value),
  });
  stats.value = await store.dispatch('aiBotIntentDecisions/stats', {
    botId: props.bot.id,
  });
};

const applyFilters = () => {
  page.value = 1;
  fetchDecisions();
};

const goToPage = nextPage => {
  page.value = nextPage;
  fetchDecisions();
};

const toggleRow = decision => {
  expandedId.value = expandedId.value === decision.id ? null : decision.id;
};

onMounted(async () => {
  fetchDecisions();
  try {
    const response = await new AiBotIntentSetsAPI(props.bot.id).get();
    const custom = response.data.find(set => set.name === 'custom');
    const active =
      response.data.find(
        set =>
          set.name === (props.bot.bot_config?.intent?.intent_set || 'generic')
      ) || custom;
    routeOptions.value = Object.keys(active?.routes || {}).map(route => ({
      value: route,
      label: route,
    }));
  } catch (error) {
    routeOptions.value = [];
  }
});

defineExpose({ filters, buildFilterParams, applyFilters, fetchDecisions });
</script>

<template>
  <div class="flex flex-col gap-4" data-testid="intent-monitor">
    <div class="flex flex-wrap items-end gap-3">
      <div class="w-48">
        <Select
          v-model="filters.route"
          :options="routeOptions"
          :placeholder="t('AI_AGENT.INTENT.FILTERS.ROUTE')"
          data-testid="filter-route"
        />
      </div>
      <div class="w-40">
        <Select
          v-model="filters.answered_by"
          :options="answeredByOptions"
          :placeholder="t('AI_AGENT.INTENT.FILTERS.ANSWERED_BY')"
          data-testid="filter-answered-by"
        />
      </div>
      <div class="w-36">
        <Select
          v-model="filters.feedback"
          :options="feedbackFilterOptions"
          :placeholder="t('AI_AGENT.INTENT.FILTERS.FEEDBACK')"
          data-testid="filter-feedback"
        />
      </div>
      <div class="w-36">
        <Input
          v-model="filters.max_confidence"
          type="number"
          :label="t('AI_AGENT.INTENT.FILTERS.MAX_CONFIDENCE')"
          data-testid="filter-max-confidence"
        />
      </div>
      <div class="w-40">
        <Input
          v-model="filters.from"
          type="date"
          :label="t('AI_AGENT.INTENT.FILTERS.FROM')"
          data-testid="filter-from"
        />
      </div>
      <div class="w-40">
        <Input
          v-model="filters.to"
          type="date"
          :label="t('AI_AGENT.INTENT.FILTERS.TO')"
          data-testid="filter-to"
        />
      </div>
      <label class="flex items-center gap-2 pb-2">
        <Switch v-model="filters.lost" data-testid="filter-lost" />
        <span class="text-sm text-n-slate-12">
          {{ t('AI_AGENT.INTENT.FILTERS.LOST') }}
        </span>
      </label>
      <Button
        :label="t('AI_AGENT.INTENT.FILTERS.APPLY')"
        data-testid="filter-apply"
        @click="applyFilters"
      />
    </div>

    <IntentStats :stats="stats" />

    <BaseTable
      :headers="tableHeaders"
      :items="records"
      :loading="uiFlags.isFetching"
      :no-data-message="t('AI_AGENT.INTENT.TABLE.EMPTY')"
      data-testid="intent-decisions-table"
    >
      <template #row="{ items }">
        <template v-for="decision in items" :key="decision.id">
          <BaseTableRow :item="decision">
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ formatTime(decision.created_at) }}
                </span>
              </BaseTableCell>
              <BaseTableCell class="max-w-0">
                <span
                  class="text-body-main text-n-slate-12 truncate block"
                  :title="decision.normalized_text"
                >
                  {{ decision.normalized_text || '—' }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span
                  v-if="decision.route"
                  class="rounded-md bg-n-brand/10 px-2 py-0.5 text-body-mini text-n-brand"
                  data-testid="decision-route"
                >
                  {{ routeBadge(decision) }}
                </span>
                <span
                  v-else
                  class="rounded-md bg-n-ruby-4 px-2 py-0.5 text-body-mini text-n-ruby-11"
                  data-testid="decision-lost"
                >
                  {{
                    t('AI_AGENT.INTENT.TABLE.LOST_BADGE', {
                      reason: decision.reason,
                    })
                  }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="flex gap-2 text-body-mini text-n-slate-11">
                  <span
                    v-for="bar in moodBars(decision)"
                    :key="bar.key"
                    :title="bar.key"
                    >{{ bar.value }}</span
                  >
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ targetLabel(decision) }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ appliedMark(decision) }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ sourceLabel(decision.answered_by || 'none') }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ latencyLabel(decision) }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <Button
                  :variant="decision.feedback !== 'none' ? 'faded' : 'ghost'"
                  :label="feedbackLabel(decision.feedback)"
                  sm
                  data-testid="decision-feedback-toggle"
                  @click="toggleRow(decision)"
                />
              </BaseTableCell>
            </template>
          </BaseTableRow>
          <tr v-if="expandedId === decision.id" data-testid="decision-expanded">
            <td :colspan="tableHeaders.length" class="px-4 py-3">
              <div class="flex flex-col gap-4">
                <div
                  v-if="probabilityRows(decision).length"
                  class="flex flex-col gap-1"
                >
                  <span class="text-body font-medium text-n-slate-12">
                    {{ t('AI_AGENT.INTENT.TABLE.PROBABILITIES') }}
                  </span>
                  <div
                    v-for="[label, probability] in probabilityRows(decision)"
                    :key="label"
                    class="flex items-center gap-2 text-body-main"
                  >
                    <span class="w-40 shrink-0 truncate text-n-slate-11">{{
                      label
                    }}</span>
                    <div class="h-2 w-40 rounded-full bg-n-solid-3">
                      <div
                        class="h-2 rounded-full bg-n-brand"
                        :style="{ width: `${Math.round(probability * 100)}%` }"
                      />
                    </div>
                    <span class="text-n-slate-11">
                      {{ percentLabel(probability) }}
                    </span>
                  </div>
                </div>
                <IntentFeedbackForm
                  :bot-id="bot.id"
                  :decision="decision"
                  :route-options="routeOptions"
                />
              </div>
            </td>
          </tr>
        </template>
      </template>
    </BaseTable>

    <div class="flex items-center justify-end gap-2">
      <Button
        :disabled="page <= 1"
        :label="t('AI_AGENT.INTENT.TABLE.PREVIOUS')"
        sm
        data-testid="page-previous"
        @click="goToPage(page - 1)"
      />
      <span class="text-body-main text-n-slate-11">
        {{ t('AI_AGENT.INTENT.TABLE.PAGE', { page, total: totalPages }) }}
      </span>
      <Button
        :disabled="page >= totalPages"
        :label="t('AI_AGENT.INTENT.TABLE.NEXT')"
        sm
        data-testid="page-next"
        @click="goToPage(page + 1)"
      />
    </div>
  </div>
</template>
